import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import { ButtonType } from 'src/components/Shared/Button';
import Button from 'src/components/Shared/Button/Button';
import CheckMark from 'src/components/Shared/CheckMark/CheckMark';
import CollegeBrandingContainer from 'src/components/Shared/CollegeBrandingContainer/CollegeBrandingContainer';
import If from 'src/components/Shared/If/If';
import config from 'src/config/config';
import { appendBasePath } from 'src/utils/nav.utils';
import {
  isAutoExchange,
  isRecurringContribute,
  isAutomaticSell,
  isAutomaticSellEdit,
  isRecurringBuy,
} from 'src/utils/transactions-util';
import ConfirmationSnapshot from '../../ConfirmationSnapshot/ConfirmationSnapshot';
import AutoExchangeWhatsNext from '../../WhatsNext/AutoExchange';
import RecurringBuyWhatsNext from '../../WhatsNext/RecurringBuy/RecurringBuyWhatsNext';
import RecurringContributeWhatsNext from '../../WhatsNext/RecurringBuy/RecurringContributeWhatsNext';
import './RecurringBuyConfirmation.scss';
import { AutoSellWhatsNext } from '../../WhatsNext/AutoSell/index';
import { useSelector } from 'react-redux';
import { isCustomerWebViewEnabled } from 'src/redux/modules/authentication/authentication.module.selectors';
import { isSalesforceRep } from 'src/redux/modules/authentication/authentication.module.selectors';

const CONFIRM_PATH_SUFFIX = '/confirm';

export interface RecurringBuyConfirmationProps {
  confirmationNumber: string;
  dispatchPageLoad: CallableFunction;
  hasAPPOnly?: boolean;
  hasAPP?: boolean;
  hasRAS?: boolean;
  transactions;
}

export const RecurringBuyConfirmation: React.VFC<
  RecurringBuyConfirmationProps
> = ({
  confirmationNumber,
  dispatchPageLoad,
  hasAPPOnly,
  hasAPP,
  hasRAS,
  transactions,
}) => {
  const customerWebViewEnabled: boolean = useSelector(isCustomerWebViewEnabled);
  const isSalesforceRepEnabled: boolean = useSelector(isSalesforceRep);
  const { pathname } = useLocation();
  const history = useHistory();

  useEffect(() => {
    // @ts-expect-error scrollintoview might not be there
    ReactDOM.findDOMNode(this)?.scrollIntoView();
    dispatchPageLoad('Confirmation');
  }, [dispatchPageLoad]);

  useEffect(() => {
    if (transactions.length === 0 && pathname.endsWith(CONFIRM_PATH_SUFFIX)) {
      history.push(
        pathname.substr(0, pathname.length - CONFIRM_PATH_SUFFIX.length)
      );
    }
  });

  const getConfirmationHeading = () => {
    if (isAutoExchange() || isRecurringBuy()) {
      return ' transaction request ';
    } else {
      return ' transaction ';
    }
  };

  return (
    <CollegeBrandingContainer>
      <div className="recurring-buy-confirmation-page">
        <div className="recurring-buy-confirmation-background-design college-branded-header">
          <span className="recurring-buy-confirmation-background-design-inner" />
        </div>
        <div className="recurring-buy-header-wrapper">
          <div className="container">
            <div className="recurring-buy-confirmation-container row">
              <div className="recurring-buy-confirmation-submit-text">
                <div className="recurring-buy-confirmation-heading">
                  Your automatic
                  {getConfirmationHeading()}
                  has been submitted.{' '}
                </div>
                <div className="recurring-buy-confirmation-subheading">{`You will receive a notification of this ${
                  isRecurringContribute()
                    ? ' automatic contribution '
                    : ' recurring plan '
                } via email.`}</div>
              </div>
              <div className="recurring-buy-snapshot-wrapper">
                <ConfirmationSnapshot
                  {...{ confirmationNumber, transactions }}
                  id="confirmation-snapshot"
                  successIcon={
                    <CheckMark className="recurring-buy-confirmation-checkmark" />
                  }
                />
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <If test={!(!customerWebViewEnabled && isSalesforceRepEnabled)}>
            <If test={isRecurringContribute()}>
              <div className="recurring-buy-confirmation-mobile-buttons">
                <a href={`${config.url}/usis/transactions/automatic`}>
                  <Button buttonType={ButtonType.SECONDARY_GHOST}>
                    Add or Edit Recurring
                  </Button>
                </a>
                <a href={`${config.url}/usis/my-accounts/dashboard`}>
                  <Button buttonType={ButtonType.SECONDARY_GHOST}>
                    Go to Dashboard
                  </Button>
                </a>
              </div>
              <RecurringContributeWhatsNext />
              <p className="lrn-contribute">2820024</p>
            </If>
            <If
              test={
                !isRecurringContribute() &&
                !isAutoExchange() &&
                !isAutomaticSell()
              }
            >
              <div className="recurring-buy-confirmation-mobile-buttons">
                <a href={`${config.url}${appendBasePath('/automatic/buy')}`}>
                  <Button buttonType={ButtonType.SECONDARY_GHOST}>
                    Submit Another Plan
                  </Button>
                </a>
                <a href={`${config.url}/usis/my-accounts/dashboard`}>
                  <Button buttonType={ButtonType.SECONDARY_GHOST}>
                    View Dashboard
                  </Button>
                </a>
              </div>
              <RecurringBuyWhatsNext
                hasAPP={hasAPP}
                hasAPPOnly={hasAPPOnly}
                hasRAS={hasRAS}
              />
              <p className="lrn-ref-number">2820024</p>
            </If>
            <If test={isAutoExchange()}>
              <div className="recurring-mobile-buttons">
                <div className="mobile-buttons-individual">
                  <a
                    href={`${config.url}${appendBasePath(
                      '/automatic/exchange'
                    )}`}
                  >
                    <Button
                      buttonType={ButtonType.SECONDARY_GHOST}
                      className="inside-button"
                    >
                      <div className="exchange-confirmation-text">
                        Add Another Recurring Exchange
                      </div>
                    </Button>
                  </a>
                </div>
                <div className="mobile-buttons-individual">
                  <a href={`${config.url}/usis/my-accounts/dashboard`}>
                    <Button
                      buttonType={ButtonType.SECONDARY_GHOST}
                      className="inside-button"
                    >
                      View Dashboard
                    </Button>
                  </a>
                </div>
              </div>
              <AutoExchangeWhatsNext />
              <p
                style={{ color: '#000', position: 'relative', bottom: '10px' }}
              >
                2791649
              </p>
            </If>
            <If test={isAutomaticSell() || isAutomaticSellEdit()}>
              <div className="recurring-mobile-buttons">
                <div className="mobile-buttons-individual">
                  <a href={`${config.url}${appendBasePath('/automatic/sell')}`}>
                    <Button
                      buttonType={ButtonType.SECONDARY_GHOST}
                      className={'inside-button'}
                    >
                      Add Another Recurring Sell
                    </Button>
                  </a>
                </div>
                <div className="mobile-buttons-individual">
                  <a href={`${config.url}/usis/my-accounts/dashboard`}>
                    <Button
                      buttonType={ButtonType.SECONDARY_GHOST}
                      className="inside-button"
                    >
                      View Dashboard
                    </Button>
                  </a>
                </div>
              </div>
              <AutoSellWhatsNext />
              <p style={{ margin: '10px 0' }}>2909931</p>
            </If>
          </If>
        </div>
      </div>
    </CollegeBrandingContainer>
  );
};
